import React, { useEffect, useMemo, useState } from "react";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import useSearchResults from "@/hooks/useSearchResults";
import {
    fetchTasksRange, fetchRemindersRange, fetchHabits,
    toggleTask, deleteTask, updateTask,
    toggleReminder, deleteReminder, updateReminder,
    toggleHabit, deleteHabit,
    toggleListItem, deleteListItem,
} from "../lib/api";
import { undoableDelete } from "../lib/undoDelete";
import { dateStr, daysInMonth, mondayDow } from "./calendar/dateUtils";
import CalendarGrid from "./calendar/CalendarGrid";
import CalendarSearchResults from "./calendar/CalendarSearchResults";
import CalendarDayDetail from "./calendar/CalendarDayDetail";

// Stable empty array reused when there's no active search query, so the
// `searchResultsView` reference doesn't churn on every render and trigger
// downstream useMemos.
const EMPTY_RESULTS = [];

// Container: owns month cursor + selected day + month data + search state and
// all mutation handlers. Rendering is delegated to ./calendar/ subcomponents
// (CalendarGrid, CalendarSearchResults, CalendarDayDetail).
// `refreshKey`: bump from the parent to force a month-data refetch after
// items are created/edited outside this component (QuickAddSheet, chat).
export default function CalendarView({
    onChange, onAddForDate, refreshKey = 0,
    onOpenEditTask, onOpenEditHabit, onOpenEditReminder,
}) {
    const todayDate = new Date();
    const [cursor, setCursor] = useState({ y: todayDate.getFullYear(), m: todayDate.getMonth() });
    const [selected, setSelected] = useState(dateStr(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate()));

    const [tasks, setTasks] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [habits, setHabits] = useState([]);

    // Calendar-scoped search. searchLoading derives from `q !== debouncedQ` —
    // no separate useState keeps this free of any synchronous set-state-in-effect.
    const [query, setQuery] = useState("");
    const q = query.trim();

    const monthStart = dateStr(cursor.y, cursor.m, 1);
    const monthEnd = dateStr(cursor.y, cursor.m, daysInMonth(cursor.y, cursor.m));

    // Loads the month's tasks/reminders/habits. Reads monthStart/monthEnd
    // from closure, both already derived from cursor.
    const reload = async () => {
        const [t, r, h] = await Promise.all([
            fetchTasksRange(monthStart, monthEnd),
            fetchRemindersRange(monthStart, monthEnd),
            fetchHabits(),
        ]);
        setTasks(t);
        setReminders(r);
        setHabits(h);
    };

    // Re-load whenever the user navigates months or the parent signals that
    // items changed elsewhere (refreshKey bump).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [cursor.y, cursor.m, refreshKey]);

    // Debounced global search — debounce + fetch each live in their own
    // custom hooks. The hook returns both results and a refetch helper
    // so mutating actions (toggle/delete) can refresh in place.
    const debouncedQ = useDebouncedValue(q, 200);
    const { results: searchResults, setResults: setSearchResults, refetch: refetchSearch } = useSearchResults(debouncedQ);
    const searchLoading = q !== "" && q !== debouncedQ;
    const searchResultsView = debouncedQ ? searchResults : EMPTY_RESULTS;

    const matchDates = useMemo(() => {
        const s = new Set();
        if (!debouncedQ) return s;
        for (const r of searchResults) if (r.date) s.add(r.date);
        return s;
    }, [debouncedQ, searchResults]);

    const tasksByDate = useMemo(() => {
        const map = {};
        tasks.forEach((t) => { (map[t.date] ||= []).push(t); });
        return map;
    }, [tasks]);
    const remindersByDate = useMemo(() => {
        const map = {};
        reminders.forEach((r) => { (map[r.date] ||= []).push(r); });
        return map;
    }, [reminders]);

    // Habits applicable for a date based on weekday + starts_on + completion lookup
    const habitsForDate = (ds) => {
        const d = new Date(ds + "T12:00:00");
        const dow = mondayDow(d);
        return habits
            .filter((h) =>
                (h.schedule_days || []).includes(dow)
                && (!h.starts_on || h.starts_on <= ds)
            )
            .map((h) => ({ ...h, completed: (h.completed_dates || []).includes(ds) }));
    };

    const goPrev = () => {
        const m = cursor.m - 1;
        if (m < 0) setCursor({ y: cursor.y - 1, m: 11 });
        else setCursor({ y: cursor.y, m });
    };
    const goNext = () => {
        const m = cursor.m + 1;
        if (m > 11) setCursor({ y: cursor.y + 1, m: 0 });
        else setCursor({ y: cursor.y, m });
    };

    const todayKey = dateStr(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
    const selDay = selected;

    // ---- day-detail handlers ----------------------------------------------
    const handleToggleTask = async (t) => {
        const res = await toggleTask(t.id, !t.completed);
        const next = res?.task || res;
        setTasks((p) => p.map((x) => (x.id === t.id ? next : x)));
        if (res?.win) {
            const { celebrateWin } = await import("../lib/wins");
            celebrateWin(res.win);
        }
        onChange?.();
    };
    const handleDeleteTask = (t) => {
        undoableDelete({
            label: "Task",
            item: t,
            apiDelete: deleteTask,
            onLocalRemove: () => setTasks((p) => p.filter((x) => x.id !== t.id)),
            onLocalRestore: () => setTasks((p) => (p.some((x) => x.id === t.id) ? p : [...p, t])),
            onCommit: () => onChange?.(),
        });
        onChange?.();
    };
    // Inline priority swap from the calendar's task list — same backend route
    // as the main list so /api/chat sees the change instantly too.
    const handleEditTaskPriority = async (t, nextPriority) => {
        const res = await updateTask(t.id, { priority: nextPriority });
        const next = res?.task || res;
        setTasks((p) => p.map((x) => (x.id === t.id ? next : x)));
        onChange?.();
    };
    const handleToggleReminder = async (r) => {
        const next = await toggleReminder(r.id, !r.completed);
        setReminders((p) => p.map((x) => (x.id === r.id ? next : x)));
        onChange?.();
    };
    const handleDeleteReminder = (r) => {
        undoableDelete({
            label: "Reminder",
            item: r,
            apiDelete: deleteReminder,
            onLocalRemove: () => setReminders((p) => p.filter((x) => x.id !== r.id)),
            onLocalRestore: () => setReminders((p) => (p.some((x) => x.id === r.id) ? p : [...p, r])),
            onCommit: () => onChange?.(),
        });
        onChange?.();
    };
    // Partial edit (title / time / lead_minutes) for a reminder from the
    // calendar's day-detail panel — mirrors the list-view pencil menu.
    const handleEditReminder = async (r, patch) => {
        const next = await updateReminder(r.id, patch);
        setReminders((p) => p.map((x) => (x.id === r.id ? next : x)));
        onChange?.();
    };
    const handleToggleHabit = async (h) => {
        // Habits toggle only meaningful for today; allow it but inform via UI
        if (selDay !== todayKey) return;
        const res = await toggleHabit(h.id);
        const next = res?.habit || res;
        setHabits((p) => p.map((x) => (x.id === h.id ? next : x)));
        if (res?.win) {
            const { celebrateWin } = await import("../lib/wins");
            celebrateWin(res.win);
        }
        onChange?.();
    };
    const handleDeleteHabit = (h) => {
        undoableDelete({
            label: "Habit",
            item: h,
            apiDelete: deleteHabit,
            onLocalRemove: () => setHabits((p) => p.filter((x) => x.id !== h.id)),
            onLocalRestore: () => setHabits((p) => (p.some((x) => x.id === h.id) ? p : [...p, h])),
            onCommit: () => onChange?.(),
        });
        onChange?.();
    };

    // ---- search-result handlers (work directly on api, then refresh) ----
    const handleResultToggle = async (r) => {
        try {
            let res;
            if (r.kind === "task") res = await toggleTask(r.id, !r.completed);
            else if (r.kind === "habit") res = await toggleHabit(r.id);
            else if (r.kind === "reminder") await toggleReminder(r.id, !r.completed);
            else if (r.kind === "list_item") await toggleListItem(r.id, !r.completed);
            if (res?.win) {
                const { celebrateWin } = await import("../lib/wins");
                celebrateWin(res.win);
            }
        } catch (e) {
            console.warn("calendar search toggle failed", e);
        }
        await refetchSearch();
        onChange?.();
        reload();
    };
    const handleResultDelete = (r) => {
        const apiCall = r.kind === "task" ? deleteTask
            : r.kind === "habit" ? deleteHabit
            : r.kind === "reminder" ? deleteReminder
            : deleteListItem;
        const label = r.kind === "list_item" ? "Item"
            : r.kind.charAt(0).toUpperCase() + r.kind.slice(1);
        undoableDelete({
            label,
            item: r,
            apiDelete: apiCall,
            onLocalRemove: () => setSearchResults((p) => p.filter((x) => x.id !== r.id)),
            onLocalRestore: () => setSearchResults((p) => (p.some((x) => x.id === r.id) ? p : [...p, r])),
            onCommit: async () => { onChange?.(); reload(); await refetchSearch(); },
        });
        onChange?.();
    };
    const jumpToDate = (ds) => {
        if (!ds) return;
        const d = new Date(ds + "T12:00:00");
        setCursor({ y: d.getFullYear(), m: d.getMonth() });
        setSelected(ds);
        setQuery(""); // clear search after jumping
    };

    return (
        <div data-testid="calendar-view">
            <CalendarGrid
                query={query}
                setQuery={setQuery}
                cursor={cursor}
                goPrev={goPrev}
                goNext={goNext}
                todayKey={todayKey}
                selectedDay={selDay}
                onSelectDay={setSelected}
                matchDates={matchDates}
                hasActiveQuery={!!q}
                tasksByDate={tasksByDate}
                remindersByDate={remindersByDate}
                habitsForDate={habitsForDate}
            />

            <div className="mt-4">
                {q ? (
                    <CalendarSearchResults
                        query={query}
                        q={q}
                        searchLoading={searchLoading}
                        results={searchResultsView}
                        todayKey={todayKey}
                        onToggle={handleResultToggle}
                        onDelete={handleResultDelete}
                        onJump={jumpToDate}
                    />
                ) : (
                    <CalendarDayDetail
                        selDay={selDay}
                        todayKey={todayKey}
                        onAddForDate={onAddForDate}
                        selTasks={tasksByDate[selDay] || []}
                        selHabits={habitsForDate(selDay)}
                        selReminders={remindersByDate[selDay] || []}
                        onToggleTask={handleToggleTask}
                        onDeleteTask={handleDeleteTask}
                        onEditTaskPriority={handleEditTaskPriority}
                        onOpenEditTask={onOpenEditTask}
                        onToggleHabit={handleToggleHabit}
                        onDeleteHabit={handleDeleteHabit}
                        onOpenEditHabit={onOpenEditHabit}
                        onToggleReminder={handleToggleReminder}
                        onDeleteReminder={handleDeleteReminder}
                        onEditReminder={handleEditReminder}
                        onOpenEditReminder={onOpenEditReminder}
                    />
                )}
            </div>
        </div>
    );
}
